/**
 * The landing page's real numbers.
 *
 * Reads GET /api/public/platform-stats, which is unauthenticated, cached server-side for five
 * minutes, and returns approved aggregate counts only.
 *
 * THE HONEST-EMPTY RULE, and it is the whole reason this hook has the shape it does.
 *
 * Nuvra is young. These counts are small right now, and some are zero. A landing page is exactly
 * where the temptation to round a small number up into something impressive lives, so this hook is
 * built so that the temptation has nowhere to act:
 *
 *   - It never invents a number, never falls back to a constant, and never renders a placeholder
 *     while loading. `stats` is null until real data arrives.
 *   - A count of zero is reported as zero. The CALLER decides whether a statistic worth zero is
 *     worth showing, and the caller here chooses not to show it, which is different from lying
 *     about it.
 *   - A failed request leaves `stats` null, so the section renders nothing. Nothing is a true
 *     statement about an unavailable number. "0 builders" would not be.
 *
 * The section that uses this is decorated with capability words when the counts are not yet worth
 * showing. Those are claims about what Nuvra DOES, which are true on day one, rather than claims
 * about how much it has done, which are not.
 */

import { useEffect, useState } from "react";

export interface PlatformStats {
  builders: number;
  projectsCreated: number;
  /** Builds that SUCCEEDED. The server never sends a count that includes failures. */
  buildsSucceeded: number;
  deployments: number;
  appVisits: number;
  buildSuccessRate: number | null;
  display: {
    builders: string;
    projectsCreated: string;
    buildsSucceeded: string;
    deployments: string;
    appVisits: string;
  };
  computedAt: string;
}

export interface PlatformStatsState {
  stats: PlatformStats | null;
  loading: boolean;
  /** True when the request finished and produced nothing usable. Never a reason to show a number. */
  failed: boolean;
}

/**
 * The threshold below which a count is not shown at all.
 *
 * Not a way to hide bad news: the number is reported truthfully by the API and visible to anyone who
 * calls it. It is a display judgement. "3 builders" on a landing page invites a reader to conclude
 * things about the product that a single afternoon of signups would overturn, and a statistic that
 * volatile is noise rather than information. When the counts clear the bar they appear, exactly as
 * measured, with no rounding up.
 */
export const MIN_DISPLAYABLE_COUNT = 50;

export function usePlatformStats(): PlatformStatsState {
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;

    (async () => {
      try {
        const res = await fetch("/api/public/platform-stats");
        if (!res.ok) throw new Error(`status ${res.status}`);
        const body = await res.json();
        if (!live) return;

        if (body?.success && body.data) {
          setStats(body.data as PlatformStats);
        } else {
          setFailed(true);
        }
      } catch {
        // Deliberately silent for the visitor. A marketing page cannot usefully act on a metrics
        // outage, and the alternative to showing nothing is showing something untrue.
        if (live) setFailed(true);
      } finally {
        if (live) setLoading(false);
      }
    })();

    return () => { live = false; };
  }, []);

  return { stats, loading, failed };
}

/** Whether a count has cleared the display threshold. Zero and small numbers stay hidden. */
export function isDisplayable(count: number): boolean {
  return Number.isFinite(count) && count >= MIN_DISPLAYABLE_COUNT;
}
